import React, { useState, useEffect, useLayoutEffect } from 'react';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Input from '@mui/material/Input';
import { IconButton, Button } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import NoticeEmitter from '../../../../NoticeUtil';

const styles = {
  root: {
    display: 'flex',
  },
  formControlSelect: {
    minWidth: 170,
    paddingRight: 20,
    paddingBottom: 16,
  },
  formControlTextfield: {
    width: 115,
    paddingRight: 20,
    paddingBottom: 16,
  },
};

// Weight is always stored and submitted in pounds. When the user picks "kg"
// from the unit dropdown, we convert their input to lbs before storing and
// convert back to kg only for display.
const LBS_PER_KG = 2.20462262;
const kgToLbs = (kg) => kg * LBS_PER_KG;
const lbsToKg = (lbs) => lbs / LBS_PER_KG;

function PackageInfo(props) {
  const [pieces, setPieces] = useState(props.pieces);
  const [weight, setWeight] = useState(props.weight);
  const [weightUnit, setWeightUnit] = useState(props.weightUnit === 'kg' ? 'kg' : 'lbs');
  // Raw text the user has in the weight field. We keep this separate from
  // the underlying lbs value so a kg-entered "10" doesn't redisplay as
  // "9.999..." after the conversion round-trip.
  const [weightInputText, setWeightInputText] = useState('');
  const [packageOptionList, setPackageOptionList] = useState(props.packageOptionList);
  const [additionalDescription, setAdditionalDescription] = useState(props.additionalDescription);
  const [overrideBillWeight, setOverrideBillWeight] = useState(props.overrideBillWeight === undefined ? '' : props.overrideBillWeight);
  // Mirrors weightInputText: the user's literal text in the override field so
  // a kg-entered "10" doesn't redisplay as "9.999..." after the conversion
  // round-trip.
  const [overrideInputText, setOverrideInputText] = useState('');
  const [defineIndividual, setDefineIndividual] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasManualEdit, setHasManualEdit] = useState(false);

  useEffect(() => {    
    const callback = (data) => {
      setHasManualEdit(true);  // Consider sync from individual pieces as manual edit
    };
    
    NoticeEmitter.addListener("PackagePiecesUpdate", callback);
    return () => NoticeEmitter.removeListener("PackagePiecesUpdate", callback);
  }, []);

  useEffect(() => {
    let p_piece = props.pieces === undefined ? 0 : props.pieces;
    setPieces(p_piece);
    const incomingWeight = props.weight === undefined ? 0 : props.weight;
    setWeight(incomingWeight);
    setPackageOptionList(props.packageOptionList);
    setAdditionalDescription(props.additionalDescription);
    setOverrideBillWeight(props.overrideBillWeight === undefined ? '' : props.overrideBillWeight);
  },[props.pieces, props.weight, props.packageOptionList, props.additionalDescription, props.overrideBillWeight]);

  useEffect(() => {
    setWeightUnit(props.weightUnit === 'kg' ? 'kg' : 'lbs');
  }, [props.weightUnit]);

  // When the package type changes, autofill flows into props.weight/pieces.
  // Drop the pinned text on the weight + override fields so the display
  // re-renders from the autofilled lbs value (converted to the current unit
  // if needed). Without this, the field keeps showing whatever the user last
  // typed before changing the package.
  useEffect(() => {
    setWeightInputText('');
    setOverrideInputText('');
  }, [props.packageSize]);

  // MUI Select renders a hidden native <input> for form submission that ships
  // with tabindex="0". SelectDisplayProps / inputProps don't reach it. Strip
  // tabbability via a MutationObserver — useLayoutEffect alone was racing with
  // MUI's own attribute applications.
  useEffect(() => {
    const strip = () => {
      document.querySelectorAll('.MuiSelect-nativeInput').forEach((el) => {
        if (el.getAttribute('tabindex') !== '-1') {
          el.setAttribute('tabindex', '-1');
        }
      });
    };
    strip();
    const observer = new MutationObserver(strip);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['tabindex'],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {    
    console.log("props.defineIndividual", props.defineIndividual);
    setDefineIndividual(props.defineIndividual);
  },[props.defineIndividual]);

useEffect(() => {
    if (isInitialLoad && props.packageSize) {
      const selectedOption = packageOptionList.find(e => e.iconTitleText === props.packageSize) || {};
      
      // Check for existing values (from quote data)
      const hasExistingValues = pieces > 0 || weight > 0;
      
      // Only use package defaults if we don't have manual edits AND don't have existing quote values
      if (!hasManualEdit && !hasExistingValues) {
        console.log("Using package defaults for new order");
        setPieces(selectedOption.fillPieces || 0);
        setWeight(selectedOption.fillWeight || 0);
      } else {
        console.log("Preserving existing values - manual edits:", hasManualEdit, "existing values:", hasExistingValues);
        // Keep current values - don't override with package defaults
      }
      setIsInitialLoad(false);
    }
  }, [props.packageSize, isInitialLoad, hasManualEdit, pieces, weight, packageOptionList]);

  useEffect(() => {
    props.onChange({
      target:{
         name: "pieces",
         value: pieces
      }
    });  
  },[pieces]);

  useEffect(() => {
    props.onChange({
      target:{
        name: "weight",
        value: weight
      }
    });
  },[weight]);

  const handleDescriptionChange = (event) => {
    props.onChange({
      target:{
        name: "additionalDescription",
        value: event.target.value
      }
    });
    setAdditionalDescription(event.target.value);
  };

  const handleWeightChange = (event) => {
    const rawValue = event.target.value;
    setWeightInputText(rawValue);

    if (rawValue === '') {
      setWeight('');
      props.onChange({
        target: {
          name: "weight",
          value: '',
          manualInput: true
        }
      });
      return;
    }

    const parsed = parseFloat(rawValue);
    if (Number.isNaN(parsed)) return;
    if (parsed < 0) return;

    // Convert kg input to lbs for storage. The backend always receives lbs.
    const lbsValue = weightUnit === 'kg' ? kgToLbs(parsed) : parsed;
    if (lbsValue > 99999) return;

    // Round to a sensible number of digits to keep state stable across renders.
    const rounded = Math.round(lbsValue * 100) / 100;
    const finalValue = rounded.toString();
    setWeight(finalValue);

    props.onChange({
      target: {
        name: "weight",
        value: finalValue,
        manualInput: true
      }
    });
  };

  // Value shown in the weight input. Prefer the user's literal text while
  // they're typing; otherwise compute it from the stored lbs in the current unit.
  const weightDisplayValue = (() => {
    if (weightInputText !== '') return weightInputText;
    if (weight === '' || weight === null || weight === undefined) return '';
    const lbs = Number(weight);
    if (Number.isNaN(lbs)) return '';
    if (weightUnit === 'kg') {
      const kg = lbsToKg(lbs);
      return (Math.round(kg * 100) / 100).toString();
    }
    return (Math.round(lbs * 100) / 100).toString();
  })();

  const handleWeightUnitChange = (event) => {
    const nextUnit = event.target.value === 'kg' ? 'kg' : 'lbs';
    // Keep whatever number is currently shown in the field — the user wants
    // "10" to stay "10" if they typed it as lbs and then realized they meant
    // kg. Reinterpret in the new unit and recompute the stored lbs for both
    // the main weight and the override bill weight.
    const displayedWeight = parseFloat(weightDisplayValue);
    const displayedOverride = parseFloat(overrideDisplayValue);
    setWeightUnit(nextUnit);

    if (!Number.isNaN(displayedWeight)) {
      const newLbs = nextUnit === 'kg' ? kgToLbs(displayedWeight) : displayedWeight;
      const roundedLbs = (Math.round(newLbs * 100) / 100).toString();
      setWeight(roundedLbs);
      setWeightInputText(displayedWeight.toString());
      // Note: NOT a manual edit — flipping the unit is just a display choice,
      // so autofill on the next package click should still apply.
      props.onChange({
        target: { name: "weight", value: roundedLbs }
      });
    }

    if (!Number.isNaN(displayedOverride)) {
      const newLbs = nextUnit === 'kg' ? kgToLbs(displayedOverride) : displayedOverride;
      const roundedLbs = (Math.round(newLbs * 100) / 100).toString();
      setOverrideBillWeight(roundedLbs);
      setOverrideInputText(displayedOverride.toString());
      props.onChange({
        target: { name: "overrideBillWeight", value: roundedLbs }
      });
    }

    props.onChange({
      target: { name: "weightUnit", value: nextUnit }
    });
  };
  
  const handlePiecesChange = (event) => {
    const rawValue = event.target.value;

    if (rawValue === '') {
      setPieces('');
      props.onChange({
        target: {
          name: "pieces",
          value: '',
          manualInput: true
        }
      });
      return;
    }

    const newPieces = parseInt(rawValue);

    // If parsing fails, ignore the input
    if (isNaN(newPieces)) {
      return;
    }

    // Clamp the value
    let finalPieces = newPieces;
    if (finalPieces < 0) finalPieces = 0;
    if (finalPieces > 999) return; // Don't allow values above max

    const finalValue = finalPieces.toString();
    setPieces(finalValue);

    props.onChange({
      target: {
        name: "pieces",
        value: finalValue,
        manualInput: true
      }
    });
  };

  const handleOverrideWeightChange = (event) => {
    const rawValue = event.target.value;
    setOverrideInputText(rawValue);

    if (rawValue === '') {
      setOverrideBillWeight('');
      props.onChange({
        target: { name: "overrideBillWeight", value: '', isManualEdit: true }
      });
      return;
    }

    const parsed = parseFloat(rawValue);
    if (Number.isNaN(parsed) || parsed < 0) return;

    // Convert kg input to lbs for storage. Backend always receives lbs.
    const lbsValue = weightUnit === 'kg' ? kgToLbs(parsed) : parsed;
    if (lbsValue > 99999) return;
    const rounded = Math.round(lbsValue * 100) / 100;
    const finalValue = rounded.toString();
    setOverrideBillWeight(finalValue);

    props.onChange({
      target: { name: "overrideBillWeight", value: finalValue, isManualEdit: true }
    });
  };

  // Display the override value in the currently selected unit, just like the
  // main weight field.
  const overrideDisplayValue = (() => {
    if (overrideInputText !== '') return overrideInputText;
    if (overrideBillWeight === '' || overrideBillWeight === null || overrideBillWeight === undefined) return '';
    const lbs = Number(overrideBillWeight);
    if (Number.isNaN(lbs)) return '';
    if (weightUnit === 'kg') {
      const kg = lbsToKg(lbs);
      return (Math.round(kg * 100) / 100).toString();
    }
    return (Math.round(lbs * 100) / 100).toString();
  })();

  const handlePackageChange = (event) => {
    props.onChange({
      target:{
        name: "packageDesc",
        value: event.target.value
      }
    });
  
    const selectedOption = packageOptionList.find(e => e.iconTitleText === event.target.value) || {};
  
    // If no manual edits, use the package defaults
    if (!hasManualEdit) {
      setPieces(selectedOption.fillPieces);
      setWeight(selectedOption.fillWeight);
      props.onChange({
        target:{
          name: "pieces",
          value: selectedOption.fillPieces
        }
      });
      props.onChange({
        target:{
          name: "weight",
          value: selectedOption.fillWeight
        }
      });
    }
    // If there have been manual edits, keep current values
    else {
      props.onChange({
        target:{
          name: "pieces",
          value: pieces
        }
      });
      props.onChange({
        target:{
          name: "weight",
          value: weight
        }
      });
    }
  
    props.onChange({
      target:{
        name: "packageSize",
        value: selectedOption.iconTitleText
      }
    }); 
  
    props.onChange({
      target:{
        name: "packageImage",
        value: selectedOption.icon_url
      }
    }); 
  };

  return (
    <div>
      <div style={{ paddingLeft: '45px',paddingTop:'10px' }}>
        <FormControl style={styles.formControlTextfield}>
          <Typography style={{fontSize: 20, fontWeight: 600, marginLeft: 30}} color="primary">Total:</Typography>
        </FormControl>
        <FormControl style={styles.formControlTextfield}>
          <InputLabel>{props.pieceDescriptionOverride || 'Pieces'}</InputLabel>
          <Input
            name="pieces"
            value={pieces}
            onChange={handlePiecesChange}
            type="number"
            startAdornment={
              <InputAdornment position="start">
                #
              </InputAdornment>
            }
          />
        </FormControl>
        <FormControl style={{...styles.formControlTextfield, width: 155}}>
          <InputLabel>Weight</InputLabel>
          <Input
            name="weight"
            value={weightDisplayValue}
            onChange={handleWeightChange}
            type="number"
            startAdornment={
              <InputAdornment position="start" sx={{ marginRight: 0 }}>
                <Select
                  value={weightUnit}
                  onChange={handleWeightUnitChange}
                  variant="standard"
                  disableUnderline
                  // Skip the unit picker in tab order — both the visible
                  // combobox AND the hidden native input MUI uses for
                  // keyboard targeting.
                  SelectDisplayProps={{ tabIndex: -1 }}
                  inputProps={{ tabIndex: -1 }}
                  sx={{
                    fontSize: 'inherit',
                    '& .MuiSelect-select': { paddingRight: '18px !important', minHeight: 'unset' },
                    '&:before, &:after': { display: 'none' },
                  }}
                >
                  <MenuItem value="lbs">lbs</MenuItem>
                  <MenuItem value="kg">kg</MenuItem>
                </Select>
              </InputAdornment>
            }
          />
        </FormControl>
        <FormControl style={{paddingRight: 20}}>
          <InputLabel>additional descriptions</InputLabel>
          <Input
            name="additionalDescription"
            value={additionalDescription}
            onChange={handleDescriptionChange}
            type="text"
          />
        </FormControl>
        {props.isEmbedded === 'yes' && (
          <FormControl style={{...styles.formControlTextfield, width: 150}}>
            <InputLabel>Override Bill Weight</InputLabel>
            <Input
              name="overrideBillWeight"
              value={overrideDisplayValue}
              onChange={handleOverrideWeightChange}
              type="number"
              startAdornment={
                <InputAdornment position="start">
                  {weightUnit}
                </InputAdornment>
              }
            />
          </FormControl>
        )}
      </div>
    </div>
  );
}

export default PackageInfo;